var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import MuiBadge from '@mui/material/Badge';
import MuiButton from '@mui/material/Button';
import React, { forwardRef } from 'react';
import colors from '../../colors';
import { applyEventProps } from '../../utils/props';
import { applyPressedCss, convertVariant } from '../Button';
import Spinner from '../Spinner';
var buttonSizeToIconVariant = {
    large: 'large',
    small: 'default',
    tiny: 'default',
};
var buttonVariantToIconColor = {
    filled: colors.white,
    outlined: colors.grey[800],
    flat: colors.grey[800],
    dark: colors.white,
};
var sizeToPixels = {
    large: '40px',
    small: '32px',
    tiny: '24px',
};
var deriveIconProps = function (_a) {
    var size = _a.size, variant = _a.variant, disabled = _a.disabled;
    return {
        variant: buttonSizeToIconVariant[size],
        color: disabled ? colors.grey[500] : buttonVariantToIconColor[variant],
    };
};
var setSize = function (size) {
    return {
        height: sizeToPixels[size],
        width: sizeToPixels[size],
        minWidth: sizeToPixels[size],
    };
};
/**
 * Material UI IconButton doesn't support
 * the same properties as Button, therefore
 * we are reusing Button component but override
 * its properties from outside :(
 */
var overrideVariantDesign = function (variant) {
    if (variant === 'dark') {
        return {
            justifyContent: 'center',
            ':hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.20)',
            },
        };
    }
    if (variant === 'flat') {
        return {
            justifyContent: 'center',
            ':hover': {
                backgroundColor: colors.grey[120],
            },
            ':active': {
                backgroundColor: colors.grey[120],
            },
        };
    }
    return {
        justifyContent: 'center',
    };
};
var IconButton = forwardRef(function (props, ref) {
    var children = props.children, _a = props.variant, variant = _a === void 0 ? 'filled' : _a, _b = props.size, size = _b === void 0 ? 'large' : _b, _c = props.loading, loading = _c === void 0 ? false : _c, _d = props.disabled, disabled = _d === void 0 ? false : _d, badgeLabel = props.badgeLabel, _e = props.badgeVariant, badgeVariant = _e === void 0 ? 'standard' : _e, onClick = props.onClick, _f = props.htmlType, htmlType = _f === void 0 ? 'button' : _f, pressed = props.pressed, rest = __rest(props, ["children", "variant", "size", "loading", "disabled", "badgeLabel", "badgeVariant", "onClick", "htmlType", "pressed"]);
    var iconProps = deriveIconProps({ size: size, variant: variant, disabled: disabled });
    var noop = function () { return undefined; };
    var renderChildren = function () {
        var IconChild = React.cloneElement(children, iconProps);
        if (loading) {
            return _jsx(Spinner, { color: iconProps.color });
        }
        if (badgeLabel && badgeLabel !== 0) {
            var scaleProp = badgeVariant === 'dot' && size === 'large' ? 'scale(1.5)' : 'scale(1)';
            var translateProp = badgeVariant === 'dot' ? 'translate(25%, -20%)' : 'translate(70%, -50%)';
            return (_jsx(MuiBadge, __assign({ badgeContent: badgeLabel, variant: badgeVariant, max: 999, sx: {
                    color: colors.blue[500],
                    '.MuiBadge-badge': {
                        borderWidth: badgeVariant === 'dot' ? '1px' : '2px',
                        fontWeight: 600,
                        borderColor: variant === 'filled' && badgeVariant === 'dot' ? colors.blue[500] : colors.white,
                        backgroundColor: badgeVariant === 'dot' ? colors.blush[500] : colors.blue[100],
                        transform: "".concat(scaleProp, " ").concat(translateProp),
                    },
                } }, { children: IconChild })));
        }
        return IconChild;
    };
    return (_jsx(MuiButton, __assign({}, rest, applyEventProps(props), { ref: ref, variant: variant === 'dark' ? 'text' : convertVariant(variant), disabled: disabled, onClick: loading ? noop : onClick, type: htmlType, sx: __assign(__assign(__assign(__assign({}, setSize(size)), { padding: '0px' }), overrideVariantDesign(variant)), applyPressedCss(variant, 'primary', pressed)) }, { children: renderChildren() })));
});
export default IconButton;
